import { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Stack,
  Group,
  Button,
  PageHeader,
  ResourceLayout,
  BottomDrawer,
  ConfirmDialog,
  Alert,
  Paper,
  Text,
  MobileListRow,
  RowActions,
  DEFAULT_PAGE_LIMIT,
  formatAddress,
  type Address,
  type ResourceQuery,
} from '@vipso/shared';
import {
  useListVendorsQuery,
  useAddUpdateVendorMutation,
  useDeleteVendorMutation,
} from '../api/tenantApi';
import {
  VendorForm,
  createEmptyVendorForm,
  isVendorPhoneValid,
  vendorFormValues,
  type VendorFormValues,
} from '../features/vendors/VendorForm';
import { useModuleAccess } from '../hooks/useModuleAccess';
import { useApprovalModuleAccess } from '../hooks/useApprovalModuleAccess';
import { useModuleEntitled, useModuleLabel, useModuleTerminology } from '../hooks/useModuleLabel';
import { ApprovalStatusBadge } from '../components/ApprovalStatusBadge';

type VendorRow = {
  id: string;
  name: string;
  phone?: string;
  email?: string;
  address?: Address | null;
  approvalStatus?: string | null;
};

function vendorListTitle(row: VendorRow): string {
  const snippet =
    row.address?.line1?.trim() ||
    row.address?.city?.trim() ||
    formatAddress(row.address);
  return snippet ? `${row.name} (${snippet})` : row.name;
}

export function VendorsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const access = useModuleAccess('vendor');
  const approvalAccess = useApprovalModuleAccess();
  const moduleLabel = useModuleLabel('vendor', 'vendors.title');
  const terms = useModuleTerminology('vendor');
  const entitled = useModuleEntitled('vendor');
  const [query, setQuery] = useState<ResourceQuery>({
    page: 1,
    pageLimit: DEFAULT_PAGE_LIMIT,
    search: '',
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<VendorRow | null>(null);
  const [form, setForm] = useState<VendorFormValues>(createEmptyVendorForm);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<VendorRow | null>(null);

  const { data, isLoading, isFetching, isError, refetch } = useListVendorsQuery({
    page: query.page,
    pageLimit: query.pageLimit,
    search: query.search || undefined,
  });
  const [save, { isLoading: saving }] = useAddUpdateVendorMutation();
  const [remove, { isLoading: deleting }] = useDeleteVendorMutation();

  const list = data?.listVendors
    ? {
        rows: (data.listVendors.rows ?? []) as VendorRow[],
        total: data.listVendors.total ?? 0,
        page: query.page,
        pageLimit: query.pageLimit,
      }
    : undefined;

  function openEdit(row: VendorRow) {
    setEditing(row);
    setPhoneError(null);
    setForm(vendorFormValues(row));
    setModalOpen(true);
  }

  async function onSave() {
    if (!isVendorPhoneValid(form)) {
      setPhoneError(t('phone.invalid'));
      return;
    }
    setPhoneError(null);
    await save({
      name: form.name,
      phone: form.phone || null,
      email: form.email || null,
      id: editing?.id,
    }).unwrap();
    setModalOpen(false);
  }

  async function handleDelete() {
    if (!confirmDelete) return;
    await remove(confirmDelete.id).unwrap();
    setConfirmDelete(null);
    setModalOpen(false);
  }

  if (!entitled) return <Navigate to="/dashboard" replace />;

  return (
    <Stack gap="md">
      <PageHeader
        title={moduleLabel}
        description={t('terminology.manageRecords', { entity: terms.plural })}
      />
      {isError ? <Alert color="red" title={t('terminology.loadFailed', { entity: terms.plural })} /> : null}
      <ResourceLayout
        data={list}
        isLoading={isLoading}
        isFetching={isFetching}
        onQueryChange={setQuery}
        getRowId={(row) => row.id}
        searchable
        searchPlaceholder={t('terminology.search', { entity: terms.plural })}
        emptyTitle={t('terminology.emptyTitle', { entity: terms.plural })}
        emptyDescription={t('terminology.emptyDescription', { entity: terms.singular })}
        onRefresh={() => void refetch()}
        refreshLabel={t('common.refresh')}
        testId="vendor-table"
        mobileView="list"
        addButton={
          access.canCreate ? (
            <Button
              testId="vendor-add"
              onClick={() => {
                setEditing(null);
                setPhoneError(null);
                setForm(createEmptyVendorForm());
                setModalOpen(true);
              }}
            >
              {t('terminology.add', { entity: terms.singular })}
            </Button>
          ) : undefined
        }
        onRowClick={(row) => navigate(`/vendors/${row.id}`)}
        rowActionsHeader={t('common.actions')}
        renderRowActions={
          access.canUpdate || access.canDelete
            ? (row) => (
                <RowActions
                  editLabel={t('common.edit')}
                  deleteLabel={t('common.delete')}
                  onEdit={access.canUpdate ? () => openEdit(row) : undefined}
                  onDelete={access.canDelete ? () => setConfirmDelete(row) : undefined}
                />
              )
            : undefined
        }
        columns={[
          { key: 'name', header: t('common.name'), render: (row) => row.name },
          { key: 'phone', header: t('common.phone'), render: (row) => row.phone || t('common.emptyValue') },
          { key: 'email', header: t('common.email'), render: (row) => row.email || t('common.emptyValue') },
          ...(approvalAccess.vendor
            ? [
                {
                  key: 'approval',
                  header: t('approval.status'),
                  render: (row: VendorRow) => (
                    <ApprovalStatusBadge status={row.approvalStatus} />
                  ),
                },
              ]
            : []),
        ]}
        renderCard={(row: VendorRow) => (
          <Paper p="md" withBorder radius="md">
            <Stack gap={4}>
              <Group justify="space-between" align="flex-start" wrap="nowrap">
                <Stack gap={4} style={{ minWidth: 0 }}>
                  <Group gap="sm">
                    <Text fw={600}>{row.name}</Text>
                    {approvalAccess.vendor ? (
                      <ApprovalStatusBadge status={row.approvalStatus} />
                    ) : null}
                  </Group>
                  <Text size="sm" c="dimmed">
                    {row.phone || t('common.noPhone')} · {row.email || t('common.noEmail')}
                  </Text>
                </Stack>
                {access.canUpdate || access.canDelete ? (
                  <RowActions
                    editLabel={t('common.edit')}
                    deleteLabel={t('common.delete')}
                    onEdit={access.canUpdate ? () => openEdit(row) : undefined}
                    onDelete={access.canDelete ? () => setConfirmDelete(row) : undefined}
                  />
                ) : null}
              </Group>
            </Stack>
          </Paper>
        )}
        renderListItem={(row: VendorRow) => (
          <MobileListRow
            title={vendorListTitle(row)}
            subtitle={row.phone || t('common.noPhone')}
            onClick={() => navigate(`/vendors/${row.id}`)}
            trailing={
              access.canUpdate || access.canDelete ? (
                <RowActions
                  editLabel={t('common.edit')}
                  deleteLabel={t('common.delete')}
                  onEdit={access.canUpdate ? () => openEdit(row) : undefined}
                  onDelete={access.canDelete ? () => setConfirmDelete(row) : undefined}
                />
              ) : undefined
            }
            showChevron={!access.canUpdate}
          />
        )}
      />
      <BottomDrawer
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? t('terminology.edit', { entity: terms.singular }) : t('terminology.add', { entity: terms.singular })}
      >
        <Stack gap="md">
          <VendorForm
            value={form}
            phoneError={phoneError}
            onChange={(next) => {
              setPhoneError(null);
              setForm(next);
            }}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setModalOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              loading={saving}
              onClick={() => void onSave()}
              disabled={!form.name.trim() || !isVendorPhoneValid(form)}
            >
              {t('common.save')}
            </Button>
          </Group>
        </Stack>
      </BottomDrawer>

      <ConfirmDialog
        opened={Boolean(confirmDelete)}
        onClose={() => setConfirmDelete(null)}
        onConfirm={() => void handleDelete()}
        title={t('terminology.deleteConfirm', { entity: terms.singular })}
        message={t('common.deleteMessage', { name: confirmDelete?.name ?? '' })}
        confirmLabel={t('common.delete')}
        loading={deleting}
        danger
      />
    </Stack>
  );
}
